import React, { useState, useEffect, useCallback } from "react";
import { usePaginated, useAsync } from "../hooks";
import { api } from "../api";
import { fmtDate } from "../utils";
import {
  useToast, useConfirm, Btn, Card, Spinner, ApiError,
  Empty, Modal, Pages, Inp, Sel, Txa,
} from "../ui";

// ─── LOCAL HELPERS ────────────────────────────────────────────────────────────
const PC = { high: "bg-rose-100 text-rose-700", medium: "bg-amber-100 text-amber-700", low: "bg-emerald-100 text-emerald-700" };
const SC = {
  pending:    "bg-amber-100 text-amber-700",
  in_progress:"bg-blue-100 text-blue-700",
  completed:  "bg-emerald-100 text-emerald-700",
  cancelled:  "bg-slate-100 text-slate-500",
};
const ROLE_COLORS = {
  owner:   "bg-amber-100 text-amber-800",
  admin:   "bg-violet-100 text-violet-800",
  agent:   "bg-blue-100 text-blue-700",
  support: "bg-sky-100 text-sky-700",
  viewer:  "bg-slate-100 text-slate-600",
};

const Badge = ({ label, colorClass }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${colorClass}`}>
    {label?.replace(/_/g, " ")}
  </span>
);

const Icon = ({ d, size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d={d}/>
  </svg>
);
const I = {
  plus:     "M12 4v16m8-8H4",
  check:    "M5 13l4 4L19 7",
  edit:     "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
  trash:    "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
  search:   "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
  user:     "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  users:    "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
  calendar: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  close:    "M6 18L18 6M6 6l12 12",
  eye:      "M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z",
};

// ─── MEMBER AVATAR ────────────────────────────────────────────────────────────
const MemberAvatar = ({ member, size = "sm" }) => {
  const s = size === "sm" ? "w-6 h-6 text-[10px]" : "w-9 h-9 text-sm";
  const initials = (member.fullName || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  if (member.avatarUrl)
    return <img src={member.avatarUrl} alt={member.fullName} title={member.fullName} className={`${s} rounded-full object-cover flex-shrink-0 border-2 border-white`}/>;
  return (
    <div title={member.fullName}
      className={`${s} rounded-full bg-gradient-to-br from-[rgb(var(--brand-rgb))] to-[rgb(var(--brand-accent-rgb))] flex items-center justify-center text-white font-bold flex-shrink-0 border-2 border-white`}>
      {initials}
    </div>
  );
};

// ─── MEMBER PICKER ────────────────────────────────────────────────────────────
// Fetches all team members, lets user multi-select with search, shows selected pills
function MemberPicker({ selected = [], onChange }) {
  const [search, setSearch]   = useState("");
  const [open, setOpen]       = useState(false);
  const [allMembers, setAll]  = useState([]);
  const [loading, setLoading] = useState(false);

  // Load all active team members once on first open
  const loadMembers = useCallback(async () => {
    if (allMembers.length) return;
    setLoading(true);
    try {
      // Fetch members across all teams — combine pages or use a large limit
      const res = await api.get("/crm/teams?page=1&limit=200");
      const teams = res.data || [];
      // For each team, fetch members — batch with Promise.all
      const memberArrays = await Promise.all(
        teams.map(t => api.get(`/crm/teams/${t._id}/members?page=1&limit=100`).then(r => r.data || []).catch(() => []))
      );
      // Flatten + deduplicate by _id
      const seen = new Set();
      const flat = memberArrays.flat().filter(m => {
        if (seen.has(m._id)) return false;
        seen.add(m._id);
        return m.status !== "inactive";
      });
      setAll(flat);
    } catch(e) { /* silently degrade */ }
    finally { setLoading(false); }
  }, [allMembers.length]);

  useEffect(() => { if (open) loadMembers(); }, [open]);

  const filtered = allMembers.filter(m =>
    !search || m.fullName.toLowerCase().includes(search.toLowerCase()) ||
    m.email.toLowerCase().includes(search.toLowerCase()) ||
    (m.jobTitle || "").toLowerCase().includes(search.toLowerCase())
  );

  const selectedSet = new Set(selected.map(m => m._id || m));

  const toggle = (member) => {
    if (selectedSet.has(member._id)) {
      onChange(selected.filter(m => (m._id || m) !== member._id));
    } else {
      onChange([...selected, member]);
    }
  };

  const removeSelected = (id) => {
    onChange(selected.filter(m => (m._id || m) !== id));
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
        Assigned Members
      </label>

      {/* Selected pills */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 rounded-xl border border-slate-200 min-h-[42px]">
          {selected.map(m => {
            const id = m._id || m;
            const name = m.fullName || id;
            return (
              <span key={id} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-medium px-2.5 py-1 rounded-lg">
                <MemberAvatar member={m} size="sm"/>
                <span className="max-w-[100px] truncate">{name}</span>
                <button type="button" onClick={() => removeSelected(id)}
                  className="text-slate-300 hover:text-rose-500 transition-colors ml-0.5">
                  <Icon d={I.close} size={11}/>
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Dropdown trigger */}
      <button type="button" onClick={() => setOpen(p => !p)}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-500 hover:border-[rgb(var(--brand-rgb))] hover:text-slate-700 transition-colors text-left">
        <Icon d={I.users} size={15} className="text-slate-400"/>
        {open ? "Close member picker" : `Assign team members${selected.length ? ` (${selected.length} selected)` : ""}`}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="p-2 border-b border-slate-100 bg-slate-50">
            <div className="relative">
              <Icon d={I.search} size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300"/>
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, email or title…"
                className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] bg-white"
                autoFocus/>
            </div>
          </div>

          <div className="max-h-52 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-6">
                <div className="w-5 h-5 border-2 border-[rgb(var(--brand-rgb))] border-t-transparent rounded-full animate-spin"/>
              </div>
            )}
            {!loading && !filtered.length && (
              <div className="text-center py-6 text-xs text-slate-400">
                {allMembers.length ? "No members match your search" : "No team members found"}
              </div>
            )}
            {!loading && filtered.map(m => {
              const isSelected = selectedSet.has(m._id);
              return (
                <button key={m._id} type="button" onClick={() => toggle(m)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0 ${isSelected ? "bg-[rgb(var(--brand-rgb)/0.05)]" : ""}`}>
                  {/* Checkbox */}
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${isSelected ? "bg-[rgb(var(--brand-rgb))] border-[rgb(var(--brand-rgb))]" : "border-slate-300"}`}>
                    {isSelected && <Icon d={I.check} size={9} className="text-white"/>}
                  </div>
                  <MemberAvatar member={m} size="md"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{m.fullName}</p>
                    <p className="text-xs text-slate-400 truncate">{m.jobTitle || m.email}</p>
                  </div>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full capitalize flex-shrink-0 ${ROLE_COLORS[m.role] || "bg-slate-100 text-slate-500"}`}>
                    {m.role}
                  </span>
                </button>
              );
            })}
          </div>

          {selected.length > 0 && (
            <div className="px-3 py-2 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
              <span className="text-xs text-slate-400">{selected.length} member{selected.length !== 1 ? "s" : ""} selected</span>
              <button type="button" onClick={() => onChange([])} className="text-xs text-rose-500 hover:text-rose-600 font-medium transition-colors">
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── TASK DETAIL PANEL ───────────────────────────────────────────────────────
function TaskDetailPanel({ task, onClose }) {
  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-start gap-2 mb-2">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5 ${
            task.priority === "high" ? "bg-rose-500" :
            task.priority === "medium" ? "bg-amber-400" : "bg-emerald-400"}`}/>
          <h3 className="text-lg font-bold text-slate-800 leading-snug">{task.taskTitle}</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap ml-4">
          <Badge label={task.status}   colorClass={SC[task.status]}   />
          <Badge label={task.priority} colorClass={PC[task.priority]} />
          {task.category && (
            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full capitalize">{task.category}</span>
          )}
        </div>
      </div>

      {/* Details grid */}
      <dl className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
        <div>
          <dt className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Client</dt>
          <dd className="text-sm font-medium text-slate-700">{task.clientId?.fullName || "–"}</dd>
          <dd className="text-xs text-slate-400">{task.clientId?.email}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Due Date</dt>
          <dd className="text-sm font-medium text-slate-700">{task.dueDate ? fmtDate(task.dueDate) : "–"}</dd>
        </div>
        {task.reminderDate && (
          <div>
            <dt className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Reminder</dt>
            <dd className="text-sm font-medium text-slate-700">{fmtDate(task.reminderDate)}</dd>
          </div>
        )}
      </dl>

      {/* Description */}
      {task.description && (
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Description</p>
          <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 rounded-xl p-3 whitespace-pre-line">
            {task.description}
          </p>
        </div>
      )}

      {/* Assigned Members */}
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
          Assigned Members ({task.assignedMembers?.length || 0})
        </p>
        {!task.assignedMembers?.length ? (
          <div className="text-sm text-slate-400 text-center py-4 bg-slate-50 rounded-xl">
            No members assigned to this task
          </div>
        ) : (
          <div className="space-y-2">
            {task.assignedMembers.map(m => (
              <div key={m._id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <MemberAvatar member={m} size="md"/>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{m.fullName}</p>
                  <p className="text-xs text-slate-400">{m.jobTitle || m.email}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full capitalize flex-shrink-0 ${ROLE_COLORS[m.role] || "bg-slate-100 text-slate-500"}`}>
                  {m.role}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TASK FORM ────────────────────────────────────────────────────────────────
function TaskForm({ initial = {}, clients = [], onSubmit, onClose, saving }) {
  const [f, sf] = useState({
    clientId:        initial.clientId?._id  || initial.clientId  || null,
    taskTitle:       initial.taskTitle      || "",
    description:     initial.description    || "",
    category:        initial.category       || "",
    dueDate:         initial.dueDate        ? initial.dueDate.split("T")[0]       : "",
    reminderDate:    initial.reminderDate   ? initial.reminderDate.split("T")[0]  : "",
    priority:        initial.priority       || "medium",
    status:          initial.status         || "pending",
    // assignedMembers: keep full objects for display; on submit send only _id strings
    assignedMembers: initial.assignedMembers || [],
  });
  const set = k => e => sf(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = e => {
    e.preventDefault();
    onSubmit({
      ...f,
      // API expects array of ObjectId strings
      assignedMembers: f.assignedMembers.map(m => m._id || m),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Sel
        label="Client *" value={f.clientId} onChange={set("clientId")} required
        options={clients.map(c => ({ value: c._id, label: c.fullName }))}/>
      <Inp
        label="Task Title *" placeholder="e.g. Send visa documents"
        value={f.taskTitle} onChange={set("taskTitle")} required/>
      <Txa
        label="Description" placeholder="Additional details…"
        value={f.description} onChange={set("description")}/>
      <div className="grid grid-cols-2 gap-4">
        <Inp label="Category"      placeholder="visa / payment / hotel…" value={f.category}    onChange={set("category")}/>
        <Inp label="Due Date"      type="date"                            value={f.dueDate}     onChange={set("dueDate")}/>
        <Inp label="Reminder Date" type="date"                            value={f.reminderDate} onChange={set("reminderDate")} max={f.dueDate || undefined}/>
        <Sel label="Priority" value={f.priority} onChange={set("priority")} options={[
          { value: "low",    label: "Low"    },
          { value: "medium", label: "Medium" },
          { value: "high",   label: "High"   },
        ]}/>
        <Sel label="Status" value={f.status} onChange={set("status")} options={[
          { value: "pending",     label: "Pending"     },
          { value: "in_progress", label: "In Progress" },
          { value: "completed",   label: "Completed"   },
          { value: "cancelled",   label: "Cancelled"   },
        ]}/>
      </div>

      {/* Team member picker — full width, below the grid */}
      <MemberPicker
        selected={f.assignedMembers}
        onChange={members => sf(p => ({ ...p, assignedMembers: members }))}/>

      <div className="flex gap-3 pt-2 border-t border-slate-100">
        <Btn type="submit" variant="primary" className="flex-1" loading={saving}>Save Task</Btn>
        <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
      </div>
    </form>
  );
}

// ─── TASKS TAB ────────────────────────────────────────────────────────────────
export function TasksTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const {
    data: tasks, total, page, totalPages,
    loading, error, setPage, refetch,
  } = usePaginated("/crm/tasks");
  const { data: clients } = usePaginated("/crm/clients", 200);

  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [modal, setModal]   = useState(null); // create | edit | view
  const [sel, setSel]       = useState(null);
  const [saving, setSaving] = useState(false);

  const shown = (tasks || []).filter(t => {
    const ms = filter === "all" || t.status === filter;
    const mq = !search ||
      t.taskTitle.toLowerCase().includes(search.toLowerCase()) ||
      t.clientId?.fullName?.toLowerCase().includes(search.toLowerCase());
    return ms && mq;
  });

  const doCreate = async form => {
    setSaving(true);
    try { await api.post("/crm/tasks", form); toast("Task created"); setModal(null); refetch(); }
    catch(e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const doEdit = async form => {
    setSaving(true);
    try { await api.patch(`/crm/tasks/${sel._id}`, form); toast("Updated"); setModal(null); refetch(); }
    catch(e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const doDelete = async id => {
    if (!(await confirm({ title: "Delete task?", message: "This task will be permanently removed.", confirmText: "Delete task" }))) return;
    try { await api.delete(`/crm/tasks/${id}`); toast("Deleted"); refetch(); }
    catch(e) { toast(e.message, "error"); }
  };

  const doToggle = async t => {
    try {
      await api.patch(`/crm/tasks/${t._id}`, { status: t.status === "completed" ? "pending" : "completed" });
      refetch();
    } catch(e) { toast(e.message, "error"); }
  };

  // Open detail — fetch fresh populated data first
  const openView = async t => {
    try { const full = await api.get(`/crm/tasks/${t._id}`); setSel(full); setModal("view"); }
    catch(e) { toast(e.message, "error"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Tasks</h1>
        <Btn variant="primary" onClick={() => setModal("create")}>
          <Icon d={I.plus} size={16}/>New Task
        </Btn>
      </div>

      <Card className="p-4 space-y-3">
        <div className="relative">
          <Icon d={I.search} size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300"/>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search tasks or clients…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"/>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all","pending","in_progress","completed","cancelled"].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize ${filter===s?"bg-[rgb(var(--brand-rgb))] text-white":"bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
              {s.replace(/_/g," ")}
            </button>
          ))}
        </div>
      </Card>

      {error   && <ApiError msg={error} onRetry={refetch}/>}
      {loading && <Spinner full/>}

      {!loading && !error && (
        <>
          {!shown.length
            ? <Empty icon="tasks" title="No tasks found" sub="Adjust filters or create a task"/>
            : <div className="space-y-2">
                {shown.map(t => (
                  <Card key={t._id} className="p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-center gap-3">
                      {/* Complete toggle */}
                      <button onClick={() => doToggle(t)}
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${t.status==="completed"?"bg-emerald-500 border-emerald-500":"border-slate-300 hover:border-[rgb(var(--brand-rgb))]"}`}>
                        {t.status === "completed" && <Icon d={I.check} size={9} className="text-white"/>}
                      </button>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <p className={`text-sm font-semibold ${t.status==="completed"?"line-through text-slate-400":"text-slate-800"}`}>
                            {t.taskTitle}
                          </p>
                          <Badge label={t.priority} colorClass={PC[t.priority]}/>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Icon d={I.user} size={11}/>{t.clientId?.fullName || "–"}
                          </span>
                          {t.dueDate && (
                            <span className="flex items-center gap-1">
                              <Icon d={I.calendar} size={11}/>{fmtDate(t.dueDate)}
                            </span>
                          )}
                          {t.category && (
                            <span className="capitalize bg-slate-100 px-1.5 py-0.5 rounded">
                              {t.category}
                            </span>
                          )}
                          {/* Assigned member avatars stack */}
                          {t.assignedMembers?.length > 0 && (
                            <span className="flex items-center gap-1 ml-1">
                              <div className="flex -space-x-1.5">
                                {t.assignedMembers.slice(0, 4).map(m => (
                                  <MemberAvatar key={m._id || m} member={m} size="sm"/>
                                ))}
                                {t.assignedMembers.length > 4 && (
                                  <div className="w-6 h-6 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-[9px] font-bold text-slate-500">
                                    +{t.assignedMembers.length - 4}
                                  </div>
                                )}
                              </div>
                              <span className="text-slate-400">{t.assignedMembers.length} assigned</span>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Badge label={t.status} colorClass={SC[t.status]}/>
                        <Btn variant="ghost" size="sm" onClick={() => openView(t)}>
                          <Icon d={I.eye} size={13}/>
                        </Btn>
                        <Btn variant="ghost" size="sm" onClick={() => { setSel(t); setModal("edit"); }}>
                          <Icon d={I.edit} size={13}/>
                        </Btn>
                        <Btn variant="danger" size="sm" onClick={() => doDelete(t._id)}>
                          <Icon d={I.trash} size={13}/>
                        </Btn>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
          }
          <Pages page={page} totalPages={totalPages} total={total} onPage={setPage} loading={loading}/>
        </>
      )}

      {/* ── Create ── */}
      {modal === "create" && (
        <Modal title="Create Task" onClose={() => setModal(null)} wide>
          <TaskForm clients={clients || []} onSubmit={doCreate} onClose={() => setModal(null)} saving={saving}/>
        </Modal>
      )}

      {/* ── Edit ── */}
      {modal === "edit" && sel && (
        <Modal title="Edit Task" onClose={() => setModal(null)} wide>
          <TaskForm initial={sel} clients={clients || []} onSubmit={doEdit} onClose={() => setModal(null)} saving={saving}/>
        </Modal>
      )}

      {/* ── View detail ── */}
      {modal === "view" && sel && (
        <Modal title="Task Details" onClose={() => setModal(null)}>
          <TaskDetailPanel task={sel} onClose={() => setModal(null)}/>
          <div className="flex gap-2 pt-4 mt-4 border-t border-slate-100">
            <Btn variant="secondary" className="flex-1" onClick={() => { setModal("edit"); }}>
              <Icon d={I.edit} size={14}/>Edit Task
            </Btn>
            <Btn variant="secondary" onClick={() => setModal(null)}>Close</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}